import React from 'react';
import { ArrowRight, MapPin, Handshake, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';

const HeroSection = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-orange-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-slate-100/60 rounded-full blur-3xl" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100/80 rounded-full">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-orange-700">
                {heroData.subtitle}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              <span className="text-orange-500">KIP ENERGY</span>
              <br />
              {heroData.title}
            </h1>

            {/* Description */}
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
              {heroData.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white group"
                asChild
              >
                <a href="#stations">
                  <MapPin className="w-5 h-5 mr-2" />
                  Trouver une Station Wayne
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-300 hover:border-orange-500 hover:text-orange-500"
                asChild
              >
                <a href="#contact">
                  <Handshake className="w-5 h-5 mr-2" />
                  Devenir Partenaire
                </a>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <span className="font-bold text-orange-500">ISO</span>
                </div>
                <span>Certifié</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <span className="font-bold text-orange-500">140</span>
                </div>
                <span>Ans d'expertise</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <span className="font-bold text-orange-500">50+</span>
                </div>
                <span>Stations</span>
              </div>
            </div>
          </div>

          {/* Visual Element - Wayne Station Image */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main image card */}
              <div className="relative z-10 rounded-3xl shadow-2xl shadow-slate-200/50 overflow-hidden border border-slate-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_5cce6493-f0af-4302-8a9d-51bbe4853e8f/artifacts/6vfqj33u_Photo%20Wayne%20Energy.avif"
                  alt="Station Wayne Energy"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                
                {/* Card footer overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-sm opacity-80">Qualité Premium</p>
                  <p className="font-semibold text-lg">Carburants Wayne</p>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -left-8 top-1/4 bg-white rounded-2xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Réseau National</p>
                    <p className="font-semibold text-slate-800">50+ Stations</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 bg-white rounded-2xl shadow-xl p-4 animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Infrastructures</p>
                    <p className="font-semibold text-slate-800">Modernes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
